import React, { useState } from "react";
import { format } from "date-fns";
import { CalendarIcon, Check, Download } from "lucide-react";
import { toast } from "sonner";

import { Button } from "components/ui/button";
import { Calendar } from "components/ui/calendar";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "components/ui/popover";

import { cn } from "lib/utils";
import { downloadReport } from "./api";

interface DateRange {
  from?: Date;
  to?: Date;
}

interface Props {
  selectedCustId?: string;
  endpoint: string;
  fileName: string;
}

const ReportsColumn = ({ selectedCustId, endpoint, fileName }: Props) => {
  const [date, setDate] = useState<DateRange>({});
  const [dateOpen, setDateOpen] = useState(false);
  const [dateTarget, setDateTarget] = useState<"from" | "to">("from");
  const [downloading, setDownloading] = useState<boolean>(false);

  const handleDateSelect = (selectedDate?: Date) => {
    if (!selectedDate) return;

    if (dateTarget === "from") {
      setDate(prev => ({
        ...prev,
        from: selectedDate,
        ...(prev.to && selectedDate > prev.to ? { to: undefined } : {}),
      }));

      setDateOpen(false);
      return;
    }

    setDate(prev => ({ ...prev, to: selectedDate }));
    setDateOpen(false);
  };

  const handleDownload = async () => {
    if (!selectedCustId) {
      toast.error("Customer not found");
      return;
    }

    try {
      setDownloading(true);
      const blob = await downloadReport(endpoint, date.from, date.to, selectedCustId);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      link.href = url;

      const fileDatePart = date.from && date.to ? `${format(date.from, "dd-MM-yyyy")}_${format(date.to, "dd-MM-yyyy")}` : date.from ? `from_${format(date.from, "dd-MM-yyyy")}` : date.to ? `upto_${format(date.to, "dd-MM-yyyy")}` : "all";

      link.download = `${fileName}_${fileDatePart}.xlsx`;
      document.body.appendChild(link);

      link.click();
      link.remove();

      window.URL.revokeObjectURL(url);

      toast.success("Report downloaded successfully", {
        style: {
          background: "#fff",
          color: "#111827",
          border: "1px solid #e5e7eb",
        },
        icon: (
          <div className="flex items-center justify-center w-5 h-5 rounded-full bg-green-100">
            <Check className="w-3 h-3 text-green-600" />
          </div>
        ),
      });
    } catch (error: any) {
      console.error(error);
      toast.error(error.message || "Failed to download report");
    } finally {
      setDownloading(false);
    }
  };

  return (
    <>
      <div className="w-[240px] shrink-0">
        <Popover open={dateOpen} onOpenChange={setDateOpen}>
          <div className="flex items-center border rounded-md bg-background h-8 overflow-hidden">
            <div className="flex divide-x w-full">
              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setDateTarget("from");
                    setDateOpen(true);
                  }}
                  className={cn(
                    "h-8 px-2 justify-start text-left font-normal rounded-none rounded-l-md hover:bg-muted/50 text-xs min-w-[118px]",
                    !date.from && "text-gray-500"
                  )}
                >
                  <CalendarIcon className="mr-2 h-3 w-3" />

                  <div className="flex flex-col min-w-0 leading-tight">
                    <span className="text-[10px] text-gray-600">From Date</span>

                    <span className="truncate">
                      {date.from ? format(date.from, "dd MMM yyyy") : ""}
                    </span>
                  </div>
                </Button>
              </PopoverTrigger>

              <PopoverTrigger asChild>
                <Button
                  variant="ghost"
                  onClick={() => {
                    setDateTarget("to");
                    setDateOpen(true);
                  }}
                  className={cn(
                    "h-8 px-2 justify-start text-left font-normal rounded-none rounded-r-md hover:bg-muted/50 text-xs min-w-[118px]",
                    !date.to && "text-gray-500"
                  )}
                >
                  <CalendarIcon className="mr-2 h-3 w-3" />

                  <div className="flex flex-col min-w-0 leading-tight">
                    <span className="text-[10px] text-gray-600">To Date</span>

                    <span className="truncate">
                      {date.to ? format(date.to, "dd MMM yyyy") : ""}
                    </span>
                  </div>
                </Button>
              </PopoverTrigger>
            </div>
          </div>

          <PopoverContent className="w-fit p-0" align="start">
            <div className="p-3">
              <Calendar
                mode="single"
                selected={dateTarget === "from" ? date.from : date.to}
                onSelect={handleDateSelect}
                numberOfMonths={2}
                disabled={d => dateTarget === "to" && date.from ? d < date.from : false}
              />
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="w-[140px] shrink-0">
        <Button
          onClick={handleDownload}
          disabled={(!date.from && !date.to) || downloading || !selectedCustId}
          className="h-8 w-full text-xs"
        >
          <Download className="h-4 w-4 mr-2" />
          {downloading ? "..." : "Download"}
        </Button>
      </div>
    </>
  );
};

export default ReportsColumn;